import { Component, ErrorInfo, ReactNode } from 'react';

import { ErrorScreen } from '@/components/error-screen';

import { captureUnexpectedError } from '@/lib/sentry';
import { ServiceError, ServiceErrorCode } from '@/services/utils/service-error';

const AUTH_ERROR_CODES = new Set<string>([
  ServiceErrorCode.InvalidCredentials,
  ServiceErrorCode.InvalidToken,
  ServiceErrorCode.UserOrganizationCtxNotFound,
  ServiceErrorCode.UserOrganizationNotFound,
  ServiceErrorCode.CsrfTokenMismatch,
  ServiceErrorCode.ForbiddenResource,
]);

function isAuthError(error: unknown): boolean {
  return error instanceof ServiceError && AUTH_ERROR_CODES.has(error.errorCode);
}

interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
}

export class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // ServiceErrors (auth redirects included) were already reported by the
    // query/mutation caches; everything else dies silently without this.
    if (error instanceof ServiceError) return;
    captureUnexpectedError(error, { componentStack: errorInfo.componentStack });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (isAuthError(error) && typeof window !== 'undefined') {
      window.location.replace('/login');
      return null;
    }

    return (
      <ErrorScreen
        status={500}
        onRetry={() => {
          if (typeof window !== 'undefined') window.location.reload();
        }}
      />
    );
  }
}
